import type {
  NotificationCategory,
  NotificationChannel,
  NotificationVariables,
} from '@tempo-io/notification-dto';
import type { Permission } from '@tempo-io/permissions';

export type DataFor<C extends NotificationCategory> = Extract<
  NotificationVariables,
  { category: C }
>['data'];

// 'entity' events participate in watch/participation resolution (they have a watchable
// subject); 'account' events are org-level facts with nothing to watch.
export type NotificationScope = 'entity' | 'account';

export interface CategoryDescriptor<C extends NotificationCategory> {
  category: C;
  actionUrl: (data: DataFor<C>, organizationId: string) => string;
  pills?: (data: DataFor<C>, ctx: { loopName?: string }) => string[];
  nonDisableable?: boolean;
  audience: Permission;
  scope: NotificationScope;
  defaultChannels: NotificationChannel[];
}

// Stored notification actionUrls and bookmarks still use /idea-management.
// Do not remove until those pre-rename rows and links have aged out.
const LEGACY_IDEA_MANAGEMENT_SEGMENT = /(^|\/)idea-management(?=\/|\?|#|$)/;

export const rewriteLegacyIdeaManagementPath = (path: string): string =>
  path.replace(LEGACY_IDEA_MANAGEMENT_SEGMENT, '$1intake');

export const extractRelativeActionUrl = (actionUrl: string): string => {
  for (const prefix of ['/org-id/', '/org/']) {
    const startPos = actionUrl.indexOf(prefix);
    if (startPos !== -1) return rewriteLegacyIdeaManagementPath(actionUrl.substring(startPos));
  }
  try {
    const parsed = new URL(actionUrl);
    return rewriteLegacyIdeaManagementPath(`${parsed.pathname}${parsed.search}${parsed.hash}`);
  } catch {
    return rewriteLegacyIdeaManagementPath(actionUrl);
  }
};

export const defineNotificationCategory = <C extends NotificationCategory>(
  category: C,
  descriptor: Omit<CategoryDescriptor<C>, 'category'>,
): CategoryDescriptor<C> => ({ category, ...descriptor });

export const integrationPills = (data: {
  integrationName?: string;
  sourceSystemType?: string;
}): string[] => {
  const pills: string[] = [];
  if (data.integrationName) pills.push(data.integrationName);
  if (data.sourceSystemType) pills.push(data.sourceSystemType);
  return pills.length > 0 ? pills : ['Integration'];
};
